import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FormActions, FormGrid, SelectField, TextField, useForm } from '../../components/Form';
import { DataTable } from '../../components/DataTable';
import { LocalDataTable } from '../../components/LocalDataTable';
import { Roles } from '../../app/providers';
import { api } from '../../lib/api';
import { money, useMutation, usePagedList, useResource } from '../../lib/hooks';
import {
  EmptyState, PageHeader, SkeletonLines, Stat, Stats,
} from '../../components/PageHeader';
import { IconAlert, IconExpense } from '../../components/Icons';
import {
  FilterBar, MONTH_OPTIONS, defaultFilters, type FilterDef,
} from '../../components/FilterBar';
import { YEAR_OPTIONS } from '../../lib/lookups';

// ---------------------------------------------------------------------------
// User edit
// ---------------------------------------------------------------------------

type UserValues = { userId: string; userName: string; email: string; role: string };

/**
 * Edit a login account.
 *
 * The server refuses two things regardless of what the form offers: nobody may promote to
 * Director here, and a Coordinator may not create another Coordinator. The UI shows the
 * options and lets the server say no, rather than duplicating the rule and risking drift.
 */
export function UserEditPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { run, busy } = useMutation();

  const existing = useResource<UserValues>(userId ? `/api/users/${userId}` : null);
  const form = useForm<UserValues>({ userId: '', userName: '', email: '', role: '' });

  useEffect(() => {
    if (existing.data) form.setValues(existing.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing.data]);

  const v = form.values;

  async function save() {
    // The legacy EditApplicationUserViewModel had [Required] on Username, Email and Role;
    // all three showed inline before the post. Reproduced here.
    const problems: Record<string, string> = {};
    if (!v.userName.trim()) problems.userName = 'Username is required.';
    if (!v.email.trim()) problems.email = 'Email is required.';
    else if (!v.email.includes('@')) problems.email = 'Enter a valid email address.';
    if (!v.role.trim()) problems.role = 'Role is required.';

    if (Object.keys(problems).length > 0) {
      form.setErrors(problems);
      document.getElementById(Object.keys(problems)[0])?.focus();
      return;
    }
    form.setErrors({});

    const ok = await run(
      () => api.put('/api/users', {
        userId: v.userId, username: v.userName, email: v.email, role: v.role,
      }),
      'User updated.',
      undefined,
      (fieldErrors) => {
        form.setErrors(fieldErrors);
        document.getElementById(Object.keys(fieldErrors)[0] ?? '')?.focus();
      },
    );

    if (ok) navigate('/users');
  }

  return (
    <>
      <div className="page__header">
        <div>
          <h2 className="page__title">Edit user</h2>
          <p className="page__subtitle">
            Changing the email marks it unconfirmed. Confirmation emails are disabled, so nothing is sent.
          </p>
        </div>
        {userId && (
          <Link className="btn" to={`/users/${userId}/sessions`}>Session history</Link>
        )}
      </div>

      <div className="card card--md">
        {existing.loading && <p>Loading…</p>}
        {existing.error && <p className="field__error">{existing.error}</p>}

        {existing.data && (
          <>
            <FormGrid>
              <TextField id="userName" label="Username" value={v.userName} onChange={form.setText('userName')} required error={form.errors.userName} />
              <TextField id="email" label="Email" type="email" value={v.email} onChange={form.setText('email')} required error={form.errors.email} />
              <SelectField
                id="role"
                label="Role"
                required
                value={v.role}
                onChange={form.setText('role')}
                error={form.errors.role}
                options={[Roles.Coordinator, Roles.Cashier, Roles.Teacher, Roles.Student]
                  .map((r) => ({ value: r, label: r }))}
                hint="Director cannot be assigned from this screen."
              />
            </FormGrid>

            <FormActions busy={busy} onSave={() => void save()} onCancel={() => navigate('/users')} />
          </>
        )}
      </div>
    </>
  );
}

type SessionRow = {
  id: number; type: string; userAgent: string; iPv4: string; createdDateTime: string;
};

export function UserSessionsPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const list = usePagedList<SessionRow>(userId ? `/api/users/${userId}/sessions` : '/api/users');

  return (
    <>
      <div className="page__header">
        <div>
          <h2 className="page__title">Session history</h2>
          <p className="page__subtitle">Sign-in and sign-out events recorded for this account.</p>
        </div>
        <button type="button" className="btn" onClick={() => navigate('/users')}>Back</button>
      </div>

      <DataTable
        columns={[
          { key: 'type', header: 'Event', render: (r) => r.type, width: '110px' },
          { key: 'when', header: 'When', render: (r) => new Date(r.createdDateTime).toLocaleString() },
          { key: 'ip', header: 'IP', render: (r) => r.iPv4 ?? '—', width: '140px' },
          { key: 'agent', header: 'User agent', render: (r) => r.userAgent ?? '—' },
        ]}
        data={list.data}
        loading={list.loading}
        error={list.error}
        rowKey={(r) => r.id}
        emptyMessage="No sessions recorded."
        onPageChange={list.setPage}
        onPageSizeChange={list.setPageSize}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Student test report
// ---------------------------------------------------------------------------

type TestScoreRow = {
  id: number;
  obtainMarks: number;
  remarks: string;
  test?: {
    id: number; topic: string; totalMarks: number; dateTime: string;
    course?: { grade?: { description: string }; subject?: { description: string }; groupName?: string };
  };
};

export function StudentTestReportPage() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const scores = useResource<TestScoreRow[]>(
    studentId ? `/api/test-scores/student/${studentId}` : null);

  const rows = scores.data ?? [];
  const totalObtained = rows.reduce((sum, r) => sum + (r.obtainMarks ?? 0), 0);
  const totalPossible = rows.reduce((sum, r) => sum + (r.test?.totalMarks ?? 0), 0);

  return (
    <>
      <div className="page__header">
        <div>
          <h2 className="page__title">Test report — STD-{studentId}</h2>
          <p className="page__subtitle">
            {totalPossible > 0
              ? `${totalObtained} of ${totalPossible} (${((totalObtained / totalPossible) * 100).toFixed(1)}%)`
              : 'No scores recorded yet.'}
          </p>
        </div>
        <button type="button" className="btn" onClick={() => navigate('/students')}>Back</button>
      </div>

      <LocalDataTable
        rows={rows}
        loading={scores.loading}
        error={scores.error}
        rowKey={(r) => r.id}
        emptyMessage="No test scores."
        columns={[
          { key: 'test', header: 'Test', render: (r) => `T-${r.test?.id}` },
          { key: 'topic', header: 'Topic', render: (r) => r.test?.topic },
          {
            key: 'course', header: 'Course',
            render: (r) => [r.test?.course?.grade?.description,
              r.test?.course?.subject?.description,
              r.test?.course?.groupName].filter(Boolean).join(' · '),
          },
          {
            key: 'date', header: 'Date',
            render: (r) => (r.test?.dateTime ? new Date(r.test.dateTime).toLocaleDateString() : '—'),
          },
          { key: 'obtained', header: 'Obtained', align: 'right', render: (r) => r.obtainMarks },
          { key: 'total', header: 'Total', align: 'right', render: (r) => r.test?.totalMarks },
          { key: 'remarks', header: 'Remarks', render: (r) => r.remarks ?? '—' },
        ]}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Expense summary
// ---------------------------------------------------------------------------

const EXPENSE_SUMMARY_FILTERS: FilterDef[] = [
  { key: 'month', label: 'Month', type: 'select', options: MONTH_OPTIONS },
  { key: 'year', label: 'Year', type: 'select', options: YEAR_OPTIONS },
];

export function ExpenseSummaryPage() {
  const summary = useResource<{
    total: number;
    count: number;
    items: { id: number; description: string; amount: number; createdDateTime: string }[];
  }>('/api/expenses/summary');

  const [filters, setFilters] = useState(defaultFilters(EXPENSE_SUMMARY_FILTERS));

  const items = (summary.data?.items ?? []).filter((item) => {
    const date = new Date(item.createdDateTime);
    if (filters.month && date.getMonth() + 1 !== Number(filters.month)) return false;
    if (filters.year && date.getFullYear() !== Number(filters.year)) return false;
    return true;
  });

  // Group by calendar month so the totals line up with how the salary months work.
  const byMonth = new Map<string, { total: number; count: number }>();
  for (const item of items) {
    const date = new Date(item.createdDateTime);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const entry = byMonth.get(key) ?? { total: 0, count: 0 };
    entry.total += item.amount;
    entry.count += 1;
    byMonth.set(key, entry);
  }

  // Latest month first: that is what a director opening this page wants to see.
  const months = [...byMonth.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([key, { total, count }]) => {
      const [year, month] = key.split('-').map(Number);
      const label = new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      return { period: key, label, total, count };
    });

  const total = items.reduce((sum, item) => sum + item.amount, 0);
  const largest = items
    .reduce<{ description: string; amount: number } | null>(
      (max, item) => (!max || item.amount > max.amount ? item : max), null);

  return (
    <>
      <PageHeader
        title="Expense Summary"
        subtitle="Director-only view of everything recorded."
      />

      {summary.loading && <div className="card"><SkeletonLines lines={4} /></div>}
      {summary.error && (
        <div className="card">
          <EmptyState
            title="Could not load the summary"
            body={summary.error}
            icon={<IconAlert />}
          />
        </div>
      )}

      {summary.data && (
        <>
          <div style={{ marginBottom: 'var(--sp-5)' }}>
            <Stats>
              <Stat
                label="Total spent"
                value={money(total)}
                tone="rose"
                icon={<IconExpense />}
              />
              {months.length > 1 && (
                // Only meaningful once the view spans more than one month - for a single
                // month it is just the total again, which is not an "average" of anything.
                <Stat
                  label="Monthly average"
                  value={money(total / months.length)}
                  tone="sky"
                  small
                />
              )}
              {largest && (
                <Stat
                  label="Largest single entry"
                  value={money(largest.amount)}
                  tone="amber"
                  small
                  hint={largest.description}
                />
              )}
            </Stats>
          </div>

          <LocalDataTable
            rows={months}
            rowKey={(r) => r.period}
            emptyMessage="No expenses recorded."
            above={(
              <FilterBar
                filters={EXPENSE_SUMMARY_FILTERS}
                values={filters}
                onApply={setFilters}
                busy={summary.loading}
              />
            )}
            toolbar={<span className="panel__title">By month</span>}
            columns={[
              { key: 'period', header: 'Month', render: (r) => r.label },
              {
                key: 'count', header: 'Entries', align: 'right',
                render: (r) => r.count,
              },
              {
                key: 'total', header: 'Total', align: 'right',
                render: (r) => <span className="money">{money(r.total)}</span>,
              },
            ]}
          />
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Course students
// ---------------------------------------------------------------------------

export function CourseStudentsPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const course = useResource<{
    id: number;
    groupName: string;
    grade?: { description: string };
    subject?: { description: string };
    studentCourses?: {
      id: number;
      studentCourseFees?: { amount: number };
      student?: { id: number; name: string; fatherName: string };
    }[];
  }>(courseId ? `/api/courses/${courseId}/students` : null);

  const students = course.data?.studentCourses ?? [];

  return (
    <>
      <div className="page__header">
        <div>
          <h2 className="page__title">Students — course C-{courseId}</h2>
          <p className="page__subtitle">
            {course.data
              ? `${course.data.grade?.description} · ${course.data.subject?.description} · ${course.data.groupName} — ${students.length} enrolled`
              : ''}
          </p>
        </div>
        <button type="button" className="btn" onClick={() => navigate('/courses')}>Back</button>
      </div>

      <LocalDataTable
        rows={students}
        loading={course.loading}
        error={course.error}
        rowKey={(r) => r.id}
        emptyMessage="Nobody is enrolled on this course."
        columns={[
          { key: 'student', header: 'Student', render: (r) => `${r.student?.name} (STD-${r.student?.id})` },
          { key: 'father', header: 'Father', render: (r) => r.student?.fatherName },
          {
            key: 'fee', header: 'Agreed fee', align: 'right',
            render: (r) => <span className="money">{money(r.studentCourseFees?.amount)}</span>,
          },
          {
            key: 'actions', header: '', align: 'right',
            render: (r) => (
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => navigate(`/fees/change/${r.student?.id}`)}
              >
                Fees
              </button>
            ),
          },
        ]}
      />
    </>
  );
}
